import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { cn } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import { PasswordManager } from "./PasswordManager";
import { NotificationBell } from "./NotificationBell";
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  Package,
  Settings,
  BarChart,
  BookOpen,
  LogOut,
  X,
  Clock,
  Lock,
  HelpCircle,
  Mail,
  Droplets,
  FileText,
  DollarSign,
  MonitorSmartphone,
  CalendarClock,
  FolderKanban
} from "lucide-react";

export function Sidebar({ open, setOpen }) {
  const location = useLocation();
  const { user, logout, isAdmin, hasModule } = useAuth();
  const [passwordManagerOpen, setPasswordManagerOpen] = useState(false);

  const navigation = [
    { name: "Pending Jobs", href: "/", icon: Clock, testId: "nav-pending-jobs", module: "pending_jobs" },
    { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard, testId: "nav-dashboard", module: "dashboard" },
    { name: "Customers", href: "/customers", icon: Users, testId: "nav-customers", module: "customers" },
    { name: "Customer Info", href: "/customer-info", icon: FileText, testId: "nav-customer-info", module: "customer_info" },
    { name: "Interactions", href: "/interactions", icon: MessageSquare, testId: "nav-interactions", module: "interactions" },
    { name: "Assets", href: "/assets", icon: Package, testId: "nav-assets", module: "assets" },
    { name: "Support Tickets", href: "/support-tickets", icon: HelpCircle, testId: "nav-support-tickets", module: "support_tickets" },
    { name: "Toner Management", href: "/toner-admin", icon: Droplets, testId: "nav-toner-management", module: "toner_management" },
    { name: "Teamviewer", href: "/teamviewer", icon: MonitorSmartphone, testId: "nav-teamviewer", module: "teamviewer" },
    { name: "Employee", href: "/employee", icon: CalendarClock, testId: "nav-employee", module: "employee" },
    { name: "OTS KB", href: "/knowledge-base", icon: BookOpen, testId: "nav-knowledge-base", module: "knowledge_base" },
    { name: "Client Billing", href: "/client-billing", icon: DollarSign, testId: "nav-client-billing", module: "client_billing" },
    { name: "Projects", href: "/projects", icon: FolderKanban, testId: "nav-projects", module: "projects" },
    { name: "Reports", href: "/reports", icon: BarChart, testId: "nav-reports", module: "reports" },
    { name: "Users", href: "/users", icon: Settings, testId: "nav-users", module: "users" },
    { name: "SMTP Settings", href: "/smtp-settings", icon: Mail, testId: "nav-smtp-settings", module: "smtp_settings" },
  ];

  const filteredNavigation = navigation.filter(item => hasModule(item.module));

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setOpen(false)}
        >
          <div className="fixed inset-0 bg-slate-600 bg-opacity-75" />
        </div>
      )}

      {/* Sidebar */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0",
        open ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-slate-200">
          <div className="flex items-center space-x-3">
            <img src="/logo.jpg" alt="Onsite Technology Solutions" className="w-8 h-8 rounded-lg object-cover" />
            <span className="text-lg font-bold text-slate-900">OTS CRM</span>
            <NotificationBell />
          </div>
          
          <button
            onClick={() => setOpen(false)}
            className="p-2 rounded-md text-slate-400 hover:text-slate-500 lg:hidden"
            data-testid="close-sidebar-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="px-4 py-6 space-y-1 flex-1">
          {filteredNavigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                data-testid={item.testId}
                className={cn(
                  "group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200",
                  isActive
                    ? "bg-blue-50 text-blue-700 border-r-2 border-blue-600"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                )}
                onClick={() => setOpen(false)}
              >
                <item.icon
                  className={cn(
                    "mr-3 h-5 w-5 flex-shrink-0",
                    isActive ? "text-blue-600" : "text-slate-400 group-hover:text-slate-500"
                  )}
                />
                {item.name}
              </Link>
            );
          })}
          
          <div className="mt-auto pt-4 space-y-2">
            <button
              onClick={() => {
                setPasswordManagerOpen(true);
                setOpen(false);
              }}
              className="group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 text-slate-600 hover:text-blue-700 hover:bg-blue-50 w-full"
              data-testid="password-manager-button"
            >
              <Lock className="mr-3 h-5 w-5 flex-shrink-0 text-slate-400 group-hover:text-blue-500" />
              Password Manager
            </button>
            
            <button
              onClick={() => {
                logout();
                setOpen(false);
              }}
              className="group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 text-slate-600 hover:text-red-700 hover:bg-red-50 w-full"
              data-testid="logout-button"
            >
              <LogOut className="mr-3 h-5 w-5 flex-shrink-0 text-slate-400 group-hover:text-red-500" />
              Logout
            </button>
          </div>
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-slate-500 to-slate-700 rounded-full flex items-center justify-center">
              <span className="text-xs font-medium text-white">
                {user?.full_name?.charAt(0)?.toUpperCase() || "U"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">
                {user?.full_name || "User"}
              </p>
              <p className="text-xs text-slate-500 truncate capitalize">
                {user?.role || "Staff"}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Password Manager Dialog */}
      <PasswordManager 
        open={passwordManagerOpen} 
        onOpenChange={setPasswordManagerOpen} 
      />
    </>
  );
}
